import React, { useEffect, useState } from "react";
import "./optionsChain.css";

export default function OptionsChain({
    options = [],
    onSelect,
    selectedOption,
    atm,
    spot,
    expiry,
    onExpiryChange,
  }) {
  const [prevPrices, setPrevPrices] = useState({});
  const [expiryType, setExpiryType] = useState("CURRENT");

  /* =========================
     Track Previous Prices
  ========================= */
  useEffect(() => {
    const priceMap = options.reduce((acc, o) => {
      acc[o.strike] = {
        ce: o.ce_ltp,
        pe: o.pe_ltp,
      };
      return acc;
    }, {});

    setPrevPrices(priceMap);
  }, [options]);

  /* =========================
     Notify Parent (Expiry Change)
  ========================= */
  useEffect(() => {
    if (onExpiryChange) {
      onExpiryChange(expiryType);
    }
  }, [expiryType, onExpiryChange]);

  /* =========================
     Helpers
  ========================= */
  const getPriceColor = (current, prev) => {
    if (prev === undefined || current === undefined) return "";
    if (current > prev) return "price-up";
    if (current < prev) return "price-down";
    return "";
  };

  const isSelected = (symbol) =>
    selectedOption?.tradingsymbol === symbol;

  const isATM = (strike) => strike === atm;

  const handleSelect = (option, type) => {
    if (!option) return;

    onSelect({
      tradingsymbol: option.symbol,
      exchange: option.exchange,
      expiry,
      lot_size: option.lot_size,
      instrument_token: option.token,
      type,
      strike: option.strike,
      price:option.price
    });
  };

  /* =========================
     Render
  ========================= */
  return (
    <div className="options-container">

      {/* ===== Header ===== */}
      <div className="options-header">    
        {/* ===== ATM Label ===== */}
        <div className="atm-label">
          ATM: <strong>{atm ?? "-"}</strong>
        </div>
        <div className="atm-label">
          SPOT: <strong>{spot ?? "-"}</strong>
        </div>
        <div className="atm-label">
          EXPIRY: <strong>{expiry ?? "-"}</strong>
        </div>
        <div className="expiry-toggle">
          {["CURRENT", "NEXT"].map((type) => (
            <button
              key={type}
              className={expiryType === type ? "active" : ""}
              onClick={() => setExpiryType(type)}
            >
              {type}
            </button>
          ))}
        </div>        
      </div>

      

      {/* ===== Column Header ===== */}
      <div className="chain-header">
        <span>CE</span>
        <span>Strike</span>
        <span>PE</span>
      </div>

      {/* ===== Options List ===== */}
      <div className="chain-list">
        {options.length === 0 ? (
          <div className="no-data">No data available</div>
        ) : (
          options.map((o, i) => {
            const prev = prevPrices[o.strike] || {};

            const ceColor = getPriceColor(o.ce_ltp, prev.ce);
            const peColor = getPriceColor(o.pe_ltp, prev.pe);

            return (
              <div
                key={`${o.strike}-${i}`}
                className={`chain-row ${
                  isATM(o.strike) ? "atm-row" : ""
                }`}
              >
                {/* ===== CE ===== */}
                <button
                  disabled={!o.ce_symbol}
                  onClick={() =>
                    handleSelect(
                      {
                        symbol: o.ce_symbol,
                        exchange: o.ce_exchange,
                        token: o.ce_token,
                        lot_size: o.lot_size,
                        strike: o.strike,
                        price: o.ce_ltp
                      },
                      "CE"
                    )
                  }
                  className={`option-btn ce
                    ${isSelected(o.ce_symbol) ? "selected" : ""}
                    ${o.ce_type?.toLowerCase() || ""}
                    ${ceColor}
                  `}
                >
                  {o.ce_ltp ?? "-"}
                </button>

                {/* ===== Strike ===== */}
                <div className="strike">{o.strike}</div>

                {/* ===== PE ===== */}
                <button
                  disabled={!o.pe_symbol}
                  onClick={() =>
                    handleSelect(
                      {
                        symbol: o.pe_symbol,
                        exchange: o.pe_exchange,
                        token: o.pe_token,
                        lot_size: o.lot_size,
                        strike: o.strike,
                        price: o.pe_ltp
                      },
                      "PE"
                    )
                  }
                  className={`option-btn pe
                    ${isSelected(o.pe_symbol) ? "selected" : ""}
                    ${o.pe_type?.toLowerCase() || ""}
                    ${peColor}
                  `}
                >
                  {o.pe_ltp ?? "-"}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
